import { isInApp, actBindAliYunPush, actSetUdSpUtil } from '@g/utils/appSDK'
import { resolveSignupReferer, clearStoredReferrer } from '@g/utils/referrerStorage'
import dayjs from 'dayjs'
import { flushReplay } from '@g/utils/sentryInstall'
import { reportEvent } from '@g/utils/aosAnalytics'
import { isH5Project, isMagicbuyProject } from '@g/utils/projectUtils'
import { persistAccessToken } from '@g/utils/authStorage'
import { presentAuthDiagnostic, reportAuthDiagnostic } from '@g/utils/authDiagnostic'
import { trackEventAndWait } from '@g/utils/track'

const state = {
}

const getters = {
}

const mutations = {
}

// 后端用「SUCCESS + name」表达需要前端接手的业务分支；这几个 name 由第三方登录页自行导向新手引导绑定手机，不额外提示
const THIRD_PARTY_BIND_NAMES = ['fbidNotExists', 'appleUserNotExists', 'lineUserNotExists']

// SUCCESS 信封不会经过拦截器的错误提示，未被页面接管的 name 在这里兜底；ERROR 信封拦截器已提示过，不重复
function showLoginHint (res) {
  if (res.config && res.config.noShowError) return
  const vueApp = window.vueApp
  if (!vueApp || typeof vueApp.$snackbar !== 'function') return
  vueApp.$snackbar(res.data.message || res.data.name, { color: 'error' })
}

function persistResponseAccessToken (res, rootState, source, { syncUid = false } = {}) {
  if (!res || !res.data || res.data.error) return false

  const model = res.data.model || {}
  if (model.status === 404 && !model.accessToken) return false
  if (!model.accessToken && res.data.name) {
    // 带 name 且没有 token 是业务分支（如 appleUserNotExists 要先绑定手机），不是存储故障，不产生 AUTH 诊断；
    // 标记 error 让调用方不走登录成功分支，页面按 name 处理
    res.data.error = res.data.name
    if (res.data.tsmdResult !== 'ERROR' && !THIRD_PARTY_BIND_NAMES.includes(res.data.name)) {
      showLoginHint(res)
    }
    return false
  }
  const inApp = isInApp()
  const result = persistAccessToken(model.accessToken, {
    project: rootState.project,
    source,
    inApp,
  })

  if (!result.ok) {
    res.data.error = 'AccessTokenPersistenceFailed'
    res.data.message = `登入失敗（${result.diagnostic.code}），請依畫面提示回報問題`
    res.data.authDiagnosticCode = result.diagnostic.code
    reportAuthDiagnostic(result.diagnostic)
    presentAuthDiagnostic(result.diagnostic)
    return false
  }

  if (inApp) {
    actBindAliYunPush(model.uid)
    actSetUdSpUtil('accessToken', encodeURIComponent(model.accessToken))
    if (syncUid) {
      actSetUdSpUtil('uid', model.uid)
    }
  }
  return true
}

const actions = {
  loginPwd ({ state, commit, rootState }, params) {
    return rootState.axios.post('/user/v1frontend/login/login-pwd', params)
      .then((res) => {
        persistResponseAccessToken(res, rootState, 'login-pwd', { syncUid: true })
        return res
      })
  },
  signupSendCaptcha ({ state, commit, rootState }, params) {
    if (isMagicbuyProject(rootState.project)) {
      params.site = 'MG'
    }
    return rootState.axios.post('/user/v1frontend/login/signup-send-captcha', params)
  },
  signup ({ state, commit, rootState }, params) {
    if (params && isMagicbuyProject(rootState.project)) {
      params.site = 'MG'
    }
    // 提交完整推广入口网址，域名按实际注册端归一；无推广来源时回退上一页网址。
    params.signupReferer = resolveSignupReferer()
    return rootState.axios.post('/user/v1frontend/login/signup', {
      ...params,
    }).then(async (res) => {
      if (persistResponseAccessToken(res, rootState, 'signup')) {
        if (dayjs(res.data.model.createdAt) > (Date.now() - 1000 * 60)) { // 剛注册的用户
          console.log('new user signup', res.data.model.createdAt)
          // 来源已随本次注册提交，清掉持久化记录，避免同一浏览器后续注册被错误归因
          clearStoredReferrer()
          if (window._ltq) {
            window._lt('send', 'cv', {
              type: 'Register'
            }, ['3dc49dbf-cd3a-45a1-a8ac-52781f6ad91b'])
          }
          // Google Analytics
          // window.gtag && window.gtag('event', 'signup', {
          //   uid: res.data.model.uid,
          // })

          // 以构建项目判断端别，避免官网 H5 模板的 window.isPc 兼容值把注册误报成 PC。
          const newUserEvent = isH5Project(rootState.project) ? 'h5_new_user' : 'pc_new_user'
          const newUserParams = { uid: res.data.model.uid }
          const trackingDone = trackEventAndWait(newUserEvent, newUserParams)
          reportEvent(newUserEvent, null, {
            uid: res.data.model.uid,
          })

          flushReplay()
          // 调用页 await signup 后才跳转，等待的必须是实际新用户事件，不能用 mb_auth_success 代替。
          await trackingDone
        }
      }
      return res
    })
  },
  resetPassword ({ state, commit, rootState }, params) {
    return rootState.axios.post('/user/v1frontend/login/reset-password', params).then((res) => {
      persistResponseAccessToken(res, rootState, 'reset-password')
      return res
    })
  },
  sendResetPasswordCaptcha ({ state, commit, rootState }, params) {
    if (isMagicbuyProject(rootState.project)) {
      params.site = 'MG'
    }
    return rootState.axios.post('/user/v1frontend/login/send-reset-password-captcha', params)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
